import React, { useRef, useEffect } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import {
  FaShieldAlt,
  FaChartLine,
  FaUsers,
  FaBolt,
  FaArrowRight,
  FaCube,
  FaCheckCircle,
  FaMicrochip,
  FaGlobe,
  FaLock,
} from "react-icons/fa";
import TopNavbar from "./TopNavbar"; // Ensure this is also responsive

/* ── Theme ── */
const T = {
  bg: "#050d1a",
  bgCard: "#0a1628",
  bgCard2: "#0d1f38",
  border: "#1a2d4a",
  accent: "#eab308",
  accentDim: "rgba(234,179,8,0.1)",
  accentGlow: "rgba(234,179,8,0.3)",
  blue: "#3b82f6",
  blueDim: "rgba(59,130,246,0.1)",
  textPrimary: "#f0f6ff",
  textSecondary: "#94a3b8",
  textMuted: "#4b6280",
  success: "#22c55e",
};

/* ── Animated 3-axis canvas background ── */
function BgCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let t = 0, id;
    
    const resize = () => { 
      c.width = window.innerWidth; 
      c.height = window.innerHeight; 
    };
    resize();
    window.addEventListener("resize", resize);

    const PARTICLE_COUNT = 40;
    const particles = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: (Math.random() - 0.5) * 800,
      y: (Math.random() - 0.5) * 800,
      z: Math.random() * 500 - 250,
      vx: (Math.random() - 0.5) * 0.25,
      vy: (Math.random() - 0.5) * 0.25,
      vz: (Math.random() - 0.5) * 0.5,
      r: Math.random() * 1.6 + 0.4,
      gold: Math.random() > 0.5,
    }));

    function project(x, y, z) {
      const fov = 550;
      const cx = c.width / 2, cy = c.height / 2;
      const s = fov / (fov + z);
      return { sx: cx + x * s, sy: cy + y * s, scale: s };
    }

    function drawGrid(yOff, color, tilt) {
      const sz = 70, cols = 16, rows = 14;
      const hw = (cols / 2) * sz, hh = (rows / 2) * sz;
      for (let r = 0; r < rows; r++) {
        for (let col = 0; col < cols; col++) {
          const bx = col * sz - hw, by = r * sz - hh;
          const wave = Math.sin((col / cols + t * 0.35) * Math.PI * 2) * 16 +
            Math.cos((r / rows + t * 0.25) * Math.PI * 2) * 10;
          const px = bx;
          const py = yOff + by * Math.cos(tilt) + wave * 0.35;
          const pz = by * Math.sin(tilt) - 320;
          const corners = [
            { x: px, y: py, z: pz }, { x: px + sz, y: py, z: pz },
            { x: px + sz, y: py + sz, z: pz + sz * 0.28 },
            { x: px, y: py + sz, z: pz + sz * 0.28 },
          ];
          const pts = corners.map(c2 => project(c2.x, c2.y, c2.z));
          const intensity = Math.max(0, Math.min(1, (wave + 16) / 32));
          const alpha = 0.03 + intensity * 0.055;
          ctx.beginPath();
          ctx.moveTo(pts[0].sx, pts[0].sy);
          pts.slice(1).forEach(p => ctx.lineTo(p.sx, p.sy));
          ctx.closePath();
          ctx.strokeStyle = color.replace("{a}", alpha.toFixed(3));
          ctx.lineWidth = 0.55;
          ctx.stroke();
        }
      }
    }

    function drawParticles() {
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.z += p.vz;
        if (p.z > 300) p.z = -250;
        if (p.z < -300) p.z = 250;
        if (Math.abs(p.x) > 450) p.vx *= -1;
        if (Math.abs(p.y) > 450) p.vy *= -1;
        const proj = project(p.x, p.y, p.z);
        const sz = Math.max(0.4, p.r * proj.scale);
        const alpha = Math.min(0.8, 0.12 + proj.scale * 0.45);
        ctx.beginPath();
        ctx.arc(proj.sx, proj.sy, sz, 0, Math.PI * 2);
        ctx.fillStyle = p.gold
          ? `rgba(234,179,8,${alpha.toFixed(2)})`
          : `rgba(96,165,250,${(alpha * 0.65).toFixed(2)})`;
        ctx.fill();
      });
    }

    function loop() {
      ctx.clearRect(0, 0, c.width, c.height);
      t += 0.004;
      drawGrid(80, "rgba(234,179,8,{a})", 0.5);
      drawGrid(-70, "rgba(96,165,250,{a})", -0.38);
      drawParticles();
      id = requestAnimationFrame(loop);
    }
    loop();
    return () => { cancelAnimationFrame(id); window.removeEventListener("resize", resize); };
  }, []);
  
  return (
    <canvas ref={ref} style={{
      position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", opacity: 0.8,
    }} />
  );
}

/* ── Scroll progress bar ── */
function ScrollBar() {
  const [prog, setProg] = React.useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      setProg((el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, height: 3, zIndex: 200,
      background: "rgba(255,255,255,0.04)",
    }}>
      <div style={{
        height: "100%", width: `${prog}%`,
        background: `linear-gradient(90deg,${T.accent},${T.blue})`,
        transition: "width 0.1s",
      }} />
    </div>
  );
}

/* ── Reusable badge ── */
function Badge({ children }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 7,
      padding: "5px 16px",
      background: T.accentDim,
      border: `1px solid rgba(234,179,8,0.28)`,
      borderRadius: 999,
      color: T.accent, fontSize: 10, fontWeight: 900,
      letterSpacing: "0.24em", textTransform: "uppercase",
      marginBottom: 20,
      alignSelf: "flex-start", // Prevents stretching in flex columns
    }}>
      {children}
    </div>
  );
}

/* ── Feature card ── */
function FeatureCard({ icon, title, desc, highlight = false, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay }}
      whileHover={{ y: -8, transition: { duration: 0.25 } }}
      style={{
        background: T.bgCard,
        border: `1px solid ${highlight ? "rgba(234,179,8,0.35)" : T.border}`,
        borderRadius: 24,
        padding: "32px 26px",
        position: "relative",
        overflow: "hidden",
        boxShadow: highlight
          ? `0 0 40px rgba(234,179,8,0.12), 0 16px 48px rgba(0,0,0,0.4)`
          : "0 8px 36px rgba(0,0,0,0.3)",
        cursor: "default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 2,
        background: highlight
          ? `linear-gradient(90deg,${T.accent},transparent)`
          : `linear-gradient(90deg,${T.blue},transparent)`,
      }} />

      <motion.div
        initial={{ x: "-120%" }}
        whileInView={{ x: "120%" }}
        viewport={{ once: true }}
        transition={{ delay: delay + 0.4, duration: 0.85 }}
        style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(105deg,transparent 30%,rgba(255,255,255,0.03) 50%,transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <motion.div
        initial={{ scale: 0, rotate: -20 }}
        whileInView={{ scale: 1, rotate: 0 }}
        viewport={{ once: true }}
        transition={{ delay: delay + 0.2, type: "spring", stiffness: 260, damping: 16 }}
        style={{
          width: 54, height: 54, borderRadius: 16,
          background: highlight
            ? `linear-gradient(135deg,#92400e,${T.accent})`
            : `linear-gradient(135deg,#1e3a5f,${T.blue})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, color: "#fff",
          marginBottom: 22,
          boxShadow: highlight ? `0 6px 20px rgba(234,179,8,0.3)` : `0 6px 20px rgba(59,130,246,0.25)`,
        }}
      >
        {icon}
      </motion.div>

      <h3 style={{
        fontSize: "1rem", fontWeight: 900, color: T.textPrimary,
        textTransform: "uppercase", letterSpacing: "-0.01em", marginBottom: 10,
      }}>
        {title}
      </h3>
      <p style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.7, margin: 0 }}>
        {desc}
      </p>
    </motion.div>
  );
}

/* ── Stat mini card ── */
function StatCard({ value, label, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85, y: 20 }}
      whileInView={{ opacity: 1, scale: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.7, type: "spring", stiffness: 200 }}
      whileHover={{ y: -4 }}
      style={{
        background: T.bgCard,
        border: `1px solid rgba(234,179,8,0.2)`,
        borderRadius: 20, padding: "22px 18px",
        position: "relative", overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg,${T.accent},transparent)`,
      }} />
      <div style={{
        fontSize: "clamp(1.5rem, 4vw, 2rem)", fontWeight: 900, color: T.accent,
        letterSpacing: "-0.04em", lineHeight: 1, marginBottom: 6,
      }}>
        {value}
      </div>
      <div style={{ fontSize: 9, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.2em" }}>
        {label}
      </div>
    </motion.div>
  );
}

/* ── containerVariants ── */
const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

export default function AboutUs() {
  const navigate = useNavigate();

  /* 3D tilt for hero card */
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-100, 100], [7, -7]);
  const rotateY = useTransform(x, [-100, 100], [-7, 7]);
  
  function handleMouse(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    x.set(event.clientX - rect.left - rect.width / 2);
    y.set(event.clientY - rect.top - rect.height / 2);
  }

  return (
    <>
      <TopNavbar />
      <ScrollBar />
      <BgCanvas />

      <div style={{
        minHeight: "100vh", width: "100%",
        background: T.bg, color: T.textPrimary,
        overflowX: "hidden",
        fontFamily: "system-ui, -apple-system, sans-serif",
        position: "relative", zIndex: 1,
      }}>

        {/* radial glows */}
        <div style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }}>
          <div style={{
            position: "absolute", top: "-8%", left: "15%",
            width: "50vw", height: "50vw", minWidth: 300, minHeight: 300, borderRadius: "50%",
            background: "radial-gradient(ellipse,rgba(234,179,8,0.06) 0%,transparent 70%)",
          }} />
          <div style={{
            position: "absolute", bottom: "5%", right: "10%",
            width: "40vw", height: "40vw", minWidth: 250, minHeight: 250, borderRadius: "50%",
            background: "radial-gradient(ellipse,rgba(59,130,246,0.07) 0%,transparent 70%)",
          }} />
        </div>

        <div style={{
          position: "relative", zIndex: 10,
          maxWidth: 1280, margin: "0 auto",
          padding: "0 clamp(16px, 4vw, 24px) 96px",
        }}>

          {/* ══════════ HERO ══════════ */}
          <motion.section
            initial="hidden"
            animate="show"
            variants={containerVariants}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
              gap: "clamp(32px, 6vw, 56px)", 
              alignItems: "center",
              paddingTop: "clamp(40px, 8vw, 80px)", 
              paddingBottom: "clamp(40px, 8vw, 80px)",
            }}
          >
            {/* Left text */}
            <motion.div variants={itemVariants} style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <Badge><FaMicrochip size={10} /> Discover 3Workers</Badge>

              <motion.h1
                variants={itemVariants}
                style={{
                  fontSize: "clamp(2.4rem, 5.5vw, 4.8rem)",
                  fontWeight: 900, lineHeight: 1.05,
                  letterSpacing: "-0.04em",
                  textTransform: "uppercase",
                  margin: "0 0 22px",
                }}
              >
                Smart Earning.{" "}
                <br />
                <span style={{ color: T.accent }}>Pure Transparency.</span>
              </motion.h1>

              <motion.p variants={itemVariants} style={{
                fontSize: "clamp(0.875rem, 2vw, 0.95rem)", color: T.textSecondary,
                lineHeight: 1.8, maxWidth: 480, marginBottom: 36,
              }}>
                3Workers transforms how you track daily returns. Built on a foundation of
                real-time data and disciplined routines, we provide a ledger-first
                experience for the modern earner.
              </motion.p>

              <motion.div variants={itemVariants} style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
                <motion.button
                  whileHover={{ scale: 1.03, boxShadow: "0 8px 32px rgba(234,179,8,0.5)" }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate("/plans")}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 10,
                    padding: "14px 30px",
                    background: T.accent, color: "#0a0d14",
                    fontWeight: 900, fontSize: 11,
                    letterSpacing: "0.16em", textTransform: "uppercase",
                    border: "none", borderRadius: 14, cursor: "pointer",
                    boxShadow: "0 4px 24px rgba(234,179,8,0.35)",
                    transition: "box-shadow 0.2s",
                    flexGrow: window.innerWidth < 400 ? 1 : 0,
                    justifyContent: "center",
                  }}
                >
                  View Our Plans <FaArrowRight size={11} />
                </motion.button>

                <Link
                  to="/login"
                  style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    padding: "14px 30px",
                    background: "transparent",
                    color: T.textSecondary,
                    fontWeight: 900, fontSize: 11,
                    letterSpacing: "0.16em", textTransform: "uppercase",
                    border: `1px solid ${T.border}`,
                    borderRadius: 14, textDecoration: "none",
                    transition: "border-color 0.2s, color 0.2s",
                    flexGrow: window.innerWidth < 400 ? 1 : 0,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textSecondary; }}
                >
                  Get Started
                </Link>
              </motion.div>
            </motion.div>

            {/* Right: 3D interaction card */}
            <motion.div
              variants={itemVariants}
              style={{ perspective: 1200 }}
              onMouseMove={handleMouse}
              onMouseLeave={() => { x.set(0); y.set(0); }}
            >
              <motion.div
                style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                whileHover={{ scale: 1.01 }}
                transition={{ type: "spring", stiffness: 260, damping: 24 }}
              >
                <div style={{
                  background: T.bgCard,
                  border: `1px solid rgba(234,179,8,0.3)`,
                  borderRadius: 28, 
                  padding: "clamp(24px, 5vw, 36px) clamp(20px, 4vw, 32px)",
                  position: "relative", overflow: "hidden",
                  boxShadow: `0 0 0 1px rgba(234,179,8,0.08), 0 32px 72px rgba(0,0,0,0.5), 0 0 48px rgba(234,179,8,0.1)`,
                }}>
                  {/* gold top line */}
                  <div style={{
                    position: "absolute", top: 0, left: 0, right: 0, height: 3,
                    background: `linear-gradient(90deg,${T.accent},${T.blue},transparent)`,
                  }} />

                  {/* decorative cube icon */}
                  <FaCube style={{
                    position: "absolute", right: -12, top: -12,
                    fontSize: 80, color: "rgba(234,179,8,0.04)",
                    transform: "rotate(12deg)",
                  }} />

                  {/* shimmer */}
                  <motion.div
                    initial={{ x: "-130%" }}
                    animate={{ x: "130%" }}
                    transition={{ delay: 1.2, duration: 1.1, ease: "easeInOut", repeat: Infinity, repeatDelay: 4 }}
                    style={{
                      position: "absolute", inset: 0,
                      background: "linear-gradient(105deg,transparent 30%,rgba(255,255,255,0.04) 50%,transparent 70%)",
                      pointerEvents: "none",
                    }}
                  />

                  <div style={{ marginBottom: 32 }}>
                    <div style={{ fontSize: 9, fontWeight: 900, color: T.accent, letterSpacing: "0.28em", textTransform: "uppercase", marginBottom: 6 }}>
                      Live Protocol
                    </div>
                    <div style={{ fontSize: "clamp(1.1rem, 3vw, 1.3rem)", fontWeight: 900, color: T.textPrimary }}>
                      Earning Engine
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                    {[
                      { label: "Daily ROI Velocity", val: "85%", color: T.accent, valN: 85 },
                      { label: "Network Stability", val: "99.9%", color: T.success, valN: 99.9 },
                      { label: "Audit Transparency", val: "100%", color: T.blue, valN: 100 },
                    ].map((stat, i) => (
                      <div key={i}>
                        <div style={{
                          display: "flex", justifyContent: "space-between",
                          fontSize: 11, fontWeight: 700, marginBottom: 7,
                        }}>
                          <span style={{ color: T.textMuted }}>{stat.label}</span>
                          <span style={{ color: stat.color, fontWeight: 900 }}>{stat.val}</span>
                        </div>
                        <div style={{
                          height: 5, background: "rgba(255,255,255,0.05)",
                          borderRadius: 999, overflow: "hidden",
                        }}>
                          <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: `${stat.valN}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.2, delay: 0.5 + i * 0.2, ease: "easeOut" }}
                            style={{
                              height: "100%",
                              background: stat.color,
                              borderRadius: 999,
                              boxShadow: `0 0 8px ${stat.color}88`,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{
                    marginTop: 28, padding: "12px 16px",
                    background: "rgba(0,0,0,0.3)",
                    border: `1px solid ${T.border}`,
                    borderRadius: 14,
                    display: "flex", alignItems: "center", gap: 10,
                  }}>
                    <motion.div
                      animate={{ opacity: [1, 0.2, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      style={{ width: 7, height: 7, borderRadius: "50%", background: T.success, flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 11, color: T.textSecondary, fontStyle: "italic", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      System syncing with global markets...
                    </span>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </motion.section>

          {/* ══════════ FEATURES GRID ══════════ */}
          <section style={{ paddingTop: 40, paddingBottom: 80 }}>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              style={{ textAlign: "center", marginBottom: 52, display: "flex", flexDirection: "column", alignItems: "center" }}
            >
              <Badge><FaBolt size={10} /> Core Infrastructure</Badge>
              <h2 style={{
                fontSize: "clamp(1.8rem, 5vw, 2.8rem)",
                fontWeight: 900, color: T.textPrimary,
                textTransform: "uppercase", letterSpacing: "-0.03em", margin: 0,
              }}>
                Built for <span style={{ color: T.accent }}>Performance</span>
              </h2>
            </motion.div>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 250px), 1fr))",
              gap: 22,
            }}>
              <FeatureCard
                icon={<FaShieldAlt />}
                title="Ironclad Security"
                desc="Enterprise-grade protection for your digital assets and personal data with military-level encryption."
                delay={0.1}
              />
              <FeatureCard
                icon={<FaBolt />}
                title="Automated Earnings"
                desc="Our precision engine calculates and credits your ledger instantly with zero latency."
                highlight
                delay={0.25}
              />
              <FeatureCard
                icon={<FaUsers />}
                title="Unified Growth"
                desc="A structured referral ecosystem designed for community success and compounding returns."
                delay={0.4}
              />
            </div>
          </section>

          {/* ══════════ WHY US ══════════ */}
          <section style={{ paddingBottom: 80 }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
              style={{
                background: T.bgCard,
                border: `1px solid rgba(234,179,8,0.18)`,
                borderRadius: 32, 
                padding: "clamp(24px, 5vw, 64px)",
                position: "relative", overflow: "hidden",
                boxShadow: "0 24px 80px rgba(0,0,0,0.45)",
              }}
            >
              {/* top accent */}
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0, height: 3,
                background: `linear-gradient(90deg,${T.accent},${T.blue},transparent)`,
              }} />

              {/* decorative chip */}
              <motion.div
                animate={{ y: [0, -16, 0], rotate: [0, 5, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                style={{ position: "absolute", top: 28, right: 36, opacity: 0.06 }}
              >
                <FaGlobe size={160} color={T.accent} />
              </motion.div>

              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
                gap: "clamp(32px, 5vw, 48px)", 
                alignItems: "center",
                position: "relative", zIndex: 1,
              }}>
                {/* left text */}
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <Badge><FaGlobe size={10} /> Why 3Workers</Badge>
                  <h2 style={{
                    fontSize: "clamp(1.8rem, 4vw, 2.6rem)",
                    fontWeight: 900, color: T.textPrimary,
                    textTransform: "uppercase", letterSpacing: "-0.03em",
                    margin: "0 0 18px", lineHeight: 1.1,
                  }}>
                    Why the world <br />
                    <span style={{ color: T.accent }}>Chooses 3Workers</span>
                  </h2>
                  <p style={{
                    fontSize: 14, color: T.textSecondary,
                    lineHeight: 1.8, marginBottom: 28, maxWidth: 420,
                  }}>
                    We believe growth should be predictable, not a gamble. Our dashboard
                    provides a surgical breakdown of every cent credited to your account.
                  </p>
                  <ul style={{ display: "flex", flexDirection: "column", gap: 14, padding: 0, margin: 0 }}>
                    {["Daily Audited Ledgers", "No Hidden Logic", "24/7 Global Support"].map((item, idx) => (
                      <motion.li
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 + idx * 0.1, duration: 0.5 }}
                        style={{
                          display: "flex", alignItems: "center", gap: 12,
                          fontSize: 13, fontWeight: 700, color: T.textPrimary,
                          listStyle: "none",
                        }}
                      >
                        <div style={{
                          width: 28, height: 28, borderRadius: 9,
                          background: T.accentDim,
                          border: "1px solid rgba(234,179,8,0.25)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: T.accent, flexShrink: 0,
                        }}>
                          <FaCheckCircle size={12} />
                        </div>
                        {item}
                      </motion.li>
                    ))}
                  </ul>
                </div>

                {/* right stat grid */}
                <div style={{
                  display: "grid", 
                  gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", 
                  gap: 16,
                }}>
                  <StatCard value="24/7" label="Monitoring" delay={0.2} />
                  <StatCard value="0.0s" label="Latency" delay={0.35} />
                  <StatCard value="100%" label="Transparency" delay={0.5} />
                  <StatCard value="$0" label="Hidden Fees" delay={0.65} />
                </div>
              </div>
            </motion.div>
          </section>

          {/* ══════════ SECURITY STRIP ══════════ */}
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{
              display: "flex", flexWrap: "wrap",
              alignItems: "center", justifyContent: "center",
              gap: "clamp(16px, 3vw, 32px)", 
              paddingBottom: 40,
            }}
          >
            {[
              { icon: <FaLock size={16} />, label: "256-bit Encryption" },
              { icon: <FaShieldAlt size={16} />, label: "DDOS Protected" },
              { icon: <FaGlobe size={16} />, label: "Global Infrastructure" },
              { icon: <FaCheckCircle size={16} />, label: "Audited Smart Logic" },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 20px",
                  background: T.bgCard,
                  border: `1px solid ${T.border}`,
                  borderRadius: 999,
                  color: T.textSecondary, fontSize: 12, fontWeight: 700,
                  flexGrow: window.innerWidth < 600 ? 1 : 0,
                  justifyContent: window.innerWidth < 600 ? "center" : "flex-start",
                }}
              >
                <span style={{ color: T.accent }}>{item.icon}</span>
                {item.label}
              </motion.div>
            ))}
          </motion.section>
        </div>

        {/* ══════════ FOOTER ══════════ */}
        <footer style={{
          background: "#030810",
          borderTop: `1px solid ${T.border}`,
          paddingTop: 52, paddingBottom: 52,
        }}>
          <div style={{
            maxWidth: 1280, margin: "0 auto",
            padding: "0 24px", textAlign: "center",
          }}>
            <div style={{
              fontSize: "1.9rem", fontWeight: 900,
              letterSpacing: "-0.04em", marginBottom: 24, color: T.textPrimary,
            }}>
              <span style={{ color: T.accent }}>3</span>Workers
            </div>

            <nav style={{ 
              display: "flex", 
              justifyContent: "center", 
              flexWrap: "wrap", 
              gap: "clamp(16px, 4vw, 32px)", 
              marginBottom: 24 
            }}>
              {[{ label: "Dashboard", to: "/" }, { label: "Plans", to: "/plans" }, { label: "Support", to: "/contact" }].map((l, i) => (
                <Link
                  key={i} to={l.to}
                  style={{
                    fontSize: 12, fontWeight: 700,
                    color: T.textMuted, textDecoration: "none",
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = T.accent}
                  onMouseLeave={e => e.currentTarget.style.color = T.textMuted}
                >
                  {l.label}
                </Link>
              ))}
            </nav>

            <div style={{
              width: 40, height: 2, margin: "0 auto 20px",
              background: `linear-gradient(90deg,transparent,${T.accent},transparent)`,
            }} />

            <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: "0.08em" }}>
              © {new Date().getFullYear()} 3Workers | All Rights Reserved
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}